import useEditBook from "api/BookResource/useEditBook";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";
import { getSubmitButtonLabel } from "api/BookResource/bookFileUpload";
import { BookFilesUploadModal } from "components/BookUpload";
import { Button, ButtonLoader, CheckBox, SelectField, TextField } from "components";
import { Form, Formik } from "formik";
import Dialog from "components/Overlays/ResponsiveDialog";
import { Tooltip } from "primereact/tooltip";
import React from "react";
import { FiEdit, FiUploadCloud } from "react-icons/fi";
import * as Yup from "yup";

const EditBook = ({ book, elementId }) => {
	const [displayBasic, setDisplayBasic] = React.useState(false);
	const [showFilesModal, setShowFilesModal] = React.useState(false);

	const { data: categories, isLoading: isCategoriesLoading } = useFetchCategories();
	const { data: genres, isLoading: isGenresLoading } = useGenres();

	const initialValues = {
		title: book?.title,
		price: book?.price,
		author: book?.author,
		isbn: book?.isbn,
		category_id: book?.category?.id,
		description: book?.description,
		genres: book?.genres[0]?.id,
		subscription: !!book?.subscription,
		sellable: !!book?.sellable,
		isFree: !!book?.isFree,
	};

	const updateSchema = Yup.object().shape({
		title: Yup.string().required("Title is required"),
		author: Yup.string().required("Author is required"),
		genres: Yup.string().required("Genre is required"),
	});

	const { mutateAsync, isLoading } = useEditBook(book?.id, () => setDisplayBasic(false));

	const handleSubmit = async (data) => {
		await mutateAsync({
			...data,
			subscription: data.subscription ? 1 : 0,
			sellable: data.sellable ? 1 : 0,
			isFree: data.isFree ? 1 : 0,
		});
	};

	const handleOpenUploadFilesFromEdit = () => {
		setDisplayBasic(false);
		setTimeout(() => setShowFilesModal(true), 200);
	};

	return (
		<div>
			<button
				className="w-8 h-8 flex items-center p-2 text-white justify-center rounded-full bg-gray-700"
				onClick={() => setDisplayBasic(true)}
				id={elementId}
			>
				<FiEdit />
			</button>
			<Tooltip
				className="green-tooltip"
				target={`#${elementId}`}
				position="bottom"
				content="Edit Book"
			/>

			<Dialog
				header={
					<div>
						<p className="text-lg font-semibold">Edit book information</p>
						<p className="text-sm text-gray-500 font-normal mt-0.5">{book?.title}</p>
					</div>
				}
				visible={displayBasic}
				onHide={() => setDisplayBasic(false)}
				style={{ width: "min(560px, 95vw)" }}
				position="center"
				draggable={false}
				modal
			>
				<Formik
					initialValues={initialValues}
					validationSchema={updateSchema}
					onSubmit={(data) => handleSubmit(data)}
				>
					{(formik) => (
						<Form>
							<TextField
								type="text"
								name="title"
								placeholder="e.g Rich Dad, Poor Dad"
								label="Title"
							/>

							<TextField
								type="text"
								name="author"
								placeholder="e.g Robert Kiyosaki"
								label="Author"
							/>

							<TextField
								type="text"
								name="isbn"
								placeholder="e.g ISN-123-453"
								label="ISBN Number"
							/>

							<TextField
								type="text"
								name="price"
								placeholder="e.g 100.99"
								label="Book Price"
							/>

							<SelectField
								name="category_id"
								label="Select Book Category"
								placeholder="e.g Academic"
							>
								{!isCategoriesLoading &&
									categories?.data?.map((category) => (
										<option value={category.id} key={category.id}>
											{category.name}
										</option>
									))}
							</SelectField>

							<SelectField
								name="genres"
								label="Select Book Collection"
								placeholder="e.g Fiction"
							>
								{!isGenresLoading &&
									genres?.data?.data?.map((genre) => (
										<option value={genre.id} key={genre.id}>
											{genre.name}
										</option>
									))}
							</SelectField>

							<TextField
								type="textarea"
								name="description"
								placeholder="Start typing..."
								label="Description"
							/>

							<CheckBox
								label="Do you want people to subscribe to this book?"
								name="subscription"
								checked={formik.values.subscription}
							/>

							<CheckBox
								label="Can people buy this book?"
								name="sellable"
								checked={formik.values.sellable}
							/>

							<CheckBox
								label="Make the book free?"
								name="isFree"
								checked={formik.values.isFree}
							/>

							<div className="mt-4 flex justify-between items-center gap-2">
								<button
									type="button"
									onClick={handleOpenUploadFilesFromEdit}
									className="inline-flex items-center gap-2 px-4 py-2 rounded border border-akgreener text-akgreener text-sm font-semibold"
								>
									<FiUploadCloud size={16} />
									Upload files
								</button>
								<div className="flex items-center gap-2">
									<Button type="reset">Cancel</Button>
									{isLoading ? (
										<ButtonLoader size={40} />
									) : (
										<Button type="submit" className="flex items-center ">
											{getSubmitButtonLabel({ isLoading, mode: "edit" })}
										</Button>
									)}
								</div>
							</div>
						</Form>
					)}
				</Formik>
			</Dialog>

			<BookFilesUploadModal
				visible={showFilesModal}
				onHide={() => setShowFilesModal(false)}
				bookId={book?.id}
				bookTitle={book?.title}
				hasExistingCover={Boolean(book?.book_cover?.url)}
				hasExistingFile={Boolean(book?.has_book_file)}
				allowSkip
			/>
		</div>
	);
};

export default EditBook;
